import type { HazardType } from './types';

// Tunable constants + the 5-board difficulty curve. All integer where it touches score.

export const LIVES_START = 3;

export const SCORING = {
  correctBase: 100,
  // M0 rebalance (verify NO-GO B1): a wrong guess must cost <= a correct rewards, so an
  // unknowing player can take informed risks instead of spiralling. Was 150.
  decoyPenalty: 60,
  perfectBoardBonus: 1000,
  timeRefundMsPerCorrect: 500,
  decoyStunMs: 250, // was 500 — softer punishment for the no-knowledge player
  purgeStunMs: 1200,
  armDurationMs: 600,
  // 2026-07-03 FACE&FURY S1 CORE-THREAD — make threading the DEFAULT path to quota. Was 2.
  // The signature verb (thread = correct CHOMP after routing through armed decoys, ×1.5) fired only
  // ~0.6×/run for a blank-slate player (funprobe median 0 threads). An empirical sweep of 17 fence-first
  // configs (scratchpad, acc=0.5 × 115 boards) proved the threshold — not the fence — is the binding
  // lever: raising fenceRatio 0.6→0.9 left avg threads flat at ~0.63 (it CLUSTERS corrects interior,
  // shortening inter-correct routes), walls only channel movement, and the sparsity that lifts threads
  // (targetCorrect↓) drops win-rate below the 45% floor. The blank-slate bot munches every believed-
  // correct greedily (acc 0.5 ⇒ ~half of cells look correct), so it rarely accumulates TWO fresh arms
  // between munches. At threshold 1 — "route THROUGH the fence" (arm ≥1 live decoy immediately before
  // committing the correct, vs. walking in over cleared/open ground) — the blank-slate median is 2.0
  // threads/run with the win-proxy preserved (~51.5%). The ×1.5 still rewards the spatial decision of
  // approaching a fenced answer through its decoy shell; it is now the regular beat the arc requires.
  // Symmetric: the rival (PlayerId 1) reads isGauntlet under the same constant. Balance version → bv=6.
  gauntletArmThreshold: 1, // armed traversals before a correct counts as "threaded"
  gauntletMult: 1.5,
  // CHOMP SURGE (owner-feedback "eating gives temporary offense", 2026-07-03). A correct munch grants a
  // brief window during which a hazard that would hit you is DESTROYED for a flat bonus instead of
  // costing a life. Integer-only (determinism): duration in ms, bonus in points (no combo interaction).
  // Duration tuned 2000→3000 (the sanctioned max) in balance iteration: the fast-moving human kept
  // out-running a 2s window, so 3s lifts its purge conversion (a human-favoring lever vs the symmetric AI).
  surgeDurationMs: 3000,
  surgePurgeBonus: 50,
} as const;

/** The information-gathering verb (verify NO-GO B1): limited reveals give the unknowing player agency.
 *  G1 fun rebalance: 3 → 4 so the no-knowledge player has more agency to convert uncertainty into
 *  points (a documented lever; helps the blank-slate case far more than the known-topic case). */
export const SCANS_PER_BOARD = 4;
export const SCAN_REVEAL_MS = 2500; // how long a scanned cell shows its true status

/** Combo multiplier tiers (1-2:×1 / 3-4:×1.5 / 5-6:×2 / 7+:×3). */
export function comboMult(combo: number): number {
  if (combo >= 7) return 3;
  if (combo >= 5) return 2;
  if (combo >= 3) return 1.5;
  return 1;
}

export type AiTierName = 'rookie' | 'sharp' | 'savant';

export interface DifficultyParams {
  rows: number;
  cols: number;
  walls: number;
  targetCorrect: number;
  quota: number;
  fenceRatio: number; // 0..0.6 — share of correct cells fenced behind decoys/walls
  softTimerMs: number;
  hazardTypes: HazardType[];
  hazardIntervalMs: number; // cadence at which a new hazard joins the board
  aiTier: AiTierName;
}

export const DEFAULT_GRID = { rows: 6, cols: 5 } as const; // portrait-first 5 wide × 6 tall

/**
 * Deception hazards — Liar (flips a tile's displayed LABEL) and Scrambler (flips/swaps answers to
 * new positions) — are OFF by default. They were the most disorienting beat for a new player, and
 * their ⚠ "warning" tells read like correctness hints when they are NOT (they are danger tells).
 * Crawler + Hunter (the honest chase enemies) still escalate the boards. Flip this to `true` to
 * bring the "flip" hazards back; every Core + renderer path for them stays intact and simply lies
 * dormant. The Daily never includes them regardless (see dailyDifficulty). Pure flag — no Date /
 * Math.random — so the determinism guard and ?seed=X replays stay reproducible either way.
 */
export const ENABLE_DECEPTION_HAZARDS = false;

/** The "flip" hazards, gated behind ENABLE_DECEPTION_HAZARDS. */
const DECEPTION_HAZARDS: HazardType[] = ['liar', 'scrambler'];

/** Allow A/B-testing the soft timer (30/35/40s) via an injected base. */
export function difficultyForBoard(
  boardIndex: number,
  base: { rows: number; cols: number } = DEFAULT_GRID,
  softTimerBaseMs = 35_000,
  includeDeception: boolean = ENABLE_DECEPTION_HAZARDS,
): DifficultyParams {
  const i = Math.max(0, Math.min(4, boardIndex));
  const hazardTiers: HazardType[][] = [
    ['crawler'],
    ['crawler', 'hunter'],
    ['crawler', 'hunter', 'liar'],
    ['crawler', 'hunter', 'liar', 'scrambler'],
    ['crawler', 'hunter', 'liar', 'scrambler'],
  ];
  const tier = hazardTiers[i] as HazardType[];
  // Deception ("flip") hazards default off (ENABLE_DECEPTION_HAZARDS). Pass includeDeception=true to
  // restore the FULL original tier in its original order — used by the renderer's ?forceDeception dev
  // override so the liar-active capture/regression shot still has a real Liar. Pure boolean, no IO.
  const hazardTypes = includeDeception ? tier : tier.filter((h) => !DECEPTION_HAZARDS.includes(h));
  const aiTier: AiTierName = i <= 1 ? 'rookie' : i <= 3 ? 'sharp' : 'savant';
  return {
    // 2026-07-02 owner-playtest retune ("board feels too small to see"): the FIRST board sheds a row
    // (5×6→5×5 phone, 6×6→6×5 tablet) so opening tiles are ~20% taller/easier to read and the match
    // grows into the full grid from board 2 — a size ramp to match the difficulty ramp. Novice/Daily
    // profiles are separate and unchanged.
    rows: i === 0 ? Math.max(4, base.rows - 1) : base.rows,
    cols: base.cols,
    walls: 1 + Math.floor(i / 2), // 1,1,2,2,3
    targetCorrect: 10 + Math.floor(i / 2), // 10,10,11,11,12
    quota: 6 + i, // 6,7,8,9,10
    // 2026-07-01 core-loop tune (re-score finding: the signature verb was RARE for the players who
    // need it — rung 0 had fenceRatio 0, so threading was IMPOSSIBLE on every match's first board;
    // the Daily was already fixed for this exact reason, see dailyDifficulty). Floor guarantees the
    // Gauntlet from board 1; quota stays reachable without threading. Strictly increasing (progression
    // smoke contract).
    // 2026-07-03 FACE&FURY S1: fence FLOOR raised on the early ramp (rung 0 0.25→0.35, rung 1
    // 0.35→0.40) so board 1/2 are unmistakably Gauntlets — the geometry of "route through the fence"
    // is real from the first board, which is what threshold=1 (see SCORING.gauntletArmThreshold) turns
    // into the default thread. Rungs 2-4 unchanged: the sweep showed higher fence CLUSTERS corrects
    // interior and slightly REDUCES threading, and rung 4 (fence 0.6) drives ~96% of the funprobe
    // aggregate, so raising it would cost thread-margin for no gain. [0.35,0.40,0.45,0.5,0.6] stays
    // strictly increasing. floor(0.35×10)=3 fenced corrects on rung 0 — a ramp, not a 100%-fenced wall.
    fenceRatio: [0.35, 0.4, 0.45, 0.5, 0.6][i] as number,
    softTimerMs: softTimerBaseMs - i * 1000,
    hazardTypes,
    // 2026-07-03 owner-feedback difficulty soften ("game is still too hard/impossible"). The blank-slate
    // (acc-0.5) funprobe win rate was the balance dial; safe-ground + CHOMP SURGE + i-frames alone only
    // reached ~48% because the rival is symmetric (it gets the same buffs and out-purges the fast-moving
    // human), so the decisive lever was hazard FREQUENCY: a small grid swarmed by ~6 roamers is the
    // "impossible" the owner felt. Slowing the cadence to ~2-3 hazards/board pulled the win rate to 55.7%
    // WITHOUT touching quota/timer/scoring/fenceRatio — the risk-it proxy held ("fair but tense").
    // NOTE: the later cold-open first-spawn GRACE (sim.ts) then re-fairs the opening and settles the proxy
    // at ~51.5% — a near-50/50 for a blind player. That drop is a proxy artifact (the grace removes the
    // early chaos the funprobe bot exploited); it is the tense-and-fair sweet spot and reaching 55-62%
    // from here would need ~1 hazard/board (over-sparse). See determinism.md 2026-07-03 for the full trail.
    // Curve: 15000, 14250, 13500, 12750, 12000 — still STRICTLY decreasing (progression smoke contract).
    hazardIntervalMs: 15_000 - i * 750,
    aiTier,
  };
}

/**
 * The Daily board is the product's front door ("Today's Gauntlet"), so it must SHOW the signature
 * Gauntlet (fenced decoys) yet be the FAIREST fenced rung for a cold player — not the unfenced
 * boardIndex-0 profile it used to inherit (fenceRatio 0 = no Gauntlet at all). Pure: no Date /
 * Math.random / DOM, so the determinism guard and a ?daily=1&seed=X replay both stay reproducible.
 */
export function dailyDifficulty(
  base: { rows: number; cols: number } = DEFAULT_GRID,
  softTimerBaseMs = 40_000,
  mutatorId?: number,
): DifficultyParams {
  const params: DifficultyParams = {
    rows: base.rows,
    cols: base.cols,
    walls: 1,
    targetCorrect: 10,
    quota: 7, // modest — between board 0 (6) and board 1 (7); reachable with 10 correct on the board
    fenceRatio: 0.3, // the signature loop: some correct cells MUST be routed to (vs 0 today)
    softTimerMs: softTimerBaseMs, // most generous base — time to route, not rush
    // BASE profile invariants (un-mutated): one non-deceptive hazard, slowest cadence. Mutator days
    // deliberately break these (TRICKSTER runs a liar, SWARM speeds the cadence) — the Daily sits
    // behind onboarding, so "cold player" protection is the BASE's job, not every twist's.
    hazardTypes: ['crawler'],
    hazardIntervalMs: 10_000, // slowest cadence (2026-07-03 soften: 9000→10000, matches the match curve's new opener)
    aiTier: 'rookie', // only matters if the player opts the rival in
  };
  const m = mutatorId !== undefined ? DAILY_MUTATORS[mutatorId] : undefined;
  return m ? { ...params, ...m.overrides } : params;
}

/**
 * Daily MUTATORS ("today's twist") — retention variety for Today's Gauntlet. Each is a one-line,
 * legible remix of the daily profile built ONLY from existing DifficultyParams levers (no new
 * mechanics, nothing to re-teach). The RENDERER picks the day's mutator (date is IO — Core stays
 * date-free); every player gets the same twist on a given UTC day, so the shared daily leaderboard
 * compares like-for-like. Every entry must keep quota reachable (targetCorrect ≥ quota + 1) —
 * validate-boards covers all entries × all seed topics.
 */
export interface DailyMutator {
  /** Stable id — the array index. Ghost links replay against it implicitly via GHOST_BALANCE_VERSION. */
  id: number;
  /** Display name (short, shouty, arcade). */
  name: string;
  /** One-line player-facing rule. */
  blurb: string;
  overrides: Partial<
    Pick<
      DifficultyParams,
      'walls' | 'targetCorrect' | 'quota' | 'fenceRatio' | 'softTimerMs' | 'hazardTypes' | 'hazardIntervalMs'
    >
  >;
}

export const DAILY_MUTATORS: readonly DailyMutator[] = [
  { id: 0, name: 'THE MAZE', blurb: 'Three walls twist every route.', overrides: { walls: 3 } },
  {
    id: 1,
    name: 'LOCKDOWN',
    blurb: 'Most answers are fenced — thread or starve.',
    overrides: { fenceRatio: 0.55 },
  },
  {
    id: 2,
    name: 'BLITZ',
    blurb: 'Twenty-six seconds. Go.',
    overrides: { softTimerMs: 26_000, quota: 6 },
  },
  {
    // NOTE: hazardTypes is a spawn CYCLE, not a concurrency count (sim spawns one per interval up
    // to its cap regardless of array length) — so SWARM's honest lever is CADENCE, and the blurb
    // says exactly that (review WARN 2026-07-02: a two-crawler cycle was a mechanical no-op).
    id: 3,
    name: 'SWARM',
    blurb: 'Hazards spawn nearly twice as fast.',
    overrides: { hazardIntervalMs: 5000 },
  },
  {
    id: 4,
    name: 'SCARCITY',
    blurb: 'Only eight answers exist. Waste nothing.',
    overrides: { targetCorrect: 8 },
  },
  {
    id: 5,
    name: 'MARATHON',
    blurb: 'Nine to clear. Pace yourself.',
    overrides: { quota: 9, softTimerMs: 48_000 },
  },
  {
    // Deception hazard is fine here: the Daily sits behind onboarding (novice players are routed to
    // training first), and TRICKSTER is the twist that most rewards the scan verb.
    id: 6,
    name: 'TRICKSTER',
    blurb: 'A Liar walks the board. Trust nothing it touches.',
    overrides: { hazardTypes: ['crawler', 'liar'], hazardIntervalMs: 8000 },
  },
];
